// Browser-side AES-GCM helpers for the Premium Family Vault.
// Encryption key is derived from the user's passphrase + per-user salt via PBKDF2.
// The server NEVER sees the passphrase, derived key, or plaintext.

const enc = new TextEncoder();
const dec = new TextDecoder();

export function bytesToB64(bytes) {
	let bin = '';
	bytes.forEach((b) => (bin += String.fromCharCode(b)));
	return btoa(bin);
}

export function b64ToBytes(b64) {
	const bin = atob(b64);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	return arr;
}

export function randomBytes(len) {
	const arr = new Uint8Array(len);
	crypto.getRandomValues(arr);
	return arr;
}

export async function deriveKey(passphrase, saltB64) {
	const salt = b64ToBytes(saltB64);
	const baseKey = await crypto.subtle.importKey(
		'raw',
		enc.encode(passphrase),
		{ name: 'PBKDF2' },
		false,
		['deriveKey'],
	);
	return crypto.subtle.deriveKey(
		{ name: 'PBKDF2', salt, iterations: 250_000, hash: 'SHA-256' },
		baseKey,
		{ name: 'AES-GCM', length: 256 },
		false,
		['encrypt', 'decrypt'],
	);
}

export async function encryptString(key, plaintext) {
	const iv = randomBytes(12);
	const ct = await crypto.subtle.encrypt(
		{ name: 'AES-GCM', iv },
		key,
		enc.encode(plaintext),
	);
	return { iv: bytesToB64(iv), cipher: bytesToB64(new Uint8Array(ct)) };
}

export async function decryptString(key, ivB64, cipherB64) {
	const pt = await crypto.subtle.decrypt(
		{ name: 'AES-GCM', iv: b64ToBytes(ivB64) },
		key,
		b64ToBytes(cipherB64),
	);
	return dec.decode(pt);
}

// Each user gets a stable per-account salt stored in localStorage so the
// derived key can be regenerated on reload. The salt by itself is not secret —
// security depends on the passphrase. The first time the user sets up the
// vault we generate a fresh salt.
export function getOrCreateSaltForUser(userId) {
	const key = `myfamshield.vault.salt.${userId}`;
	let salt = localStorage.getItem(key);
	if (!salt) {
		salt = bytesToB64(randomBytes(16));
		localStorage.setItem(key, salt);
	}
	return salt;
}

// Recovery package: a printable JSON blob that contains the salt + a clear
// note. Without the user's passphrase the file alone cannot decrypt anything.
export function buildRecoveryPackage(userId, salt) {
	return {
		app: 'MyFamShield',
		user_id: userId,
		salt,
		generated_at: new Date().toISOString(),
		warning:
			'Keep this file safe and offline. It is one of two halves needed to recover your vault. The other half is your passphrase, which only you know. If you lose both your recovery key and your passphrase, your encrypted information CANNOT be recovered.',
	};
}
